import React, { useCallback, useEffect, useState } from 'react';
import {
  createReferralPanelContact,
  deleteReferralPanelContact,
  listReferralPanelContacts,
  updateReferralPanelContact,
} from '../../services/adminReferralPanelService';
import './ReferralPanel.css';

const emptyForm = () => ({
  firstName: '',
  lastName: '',
  englishName: '',
  company: '',
  email: '',
  phone: '',
  status: 'ACTIVE',
  notes: '',
});

const NOTES_MAX_LEN = 16384;

function formatApiError(err) {
  const d = err.response?.data;
  if (typeof d?.message === 'string') return d.message;
  if (typeof d?.error === 'string') return d.error;
  return err.message || 'Something went wrong.';
}

function contactLabel(row) {
  return row.displayName || [row.firstName, row.lastName].filter(Boolean).join(' ') || 'this contact';
}

export default function ReferralPanel() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await listReferralPanelContacts();
      setGroups(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(formatApiError(e));
      setGroups([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const totalContacts = groups.reduce((n, g) => n + (Array.isArray(g.contacts) ? g.contacts.length : 0), 0);

  const openCreate = () => {
    setModalMode('create');
    setEditId(null);
    setForm(emptyForm());
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setModalMode('edit');
    setEditId(row.id);
    setForm({
      firstName: row.firstName ?? '',
      lastName: row.lastName ?? '',
      englishName: row.englishName ?? '',
      company: row.company ?? '',
      email: row.email ?? '',
      phone: row.phone ?? '',
      status: row.status === 'INACTIVE' ? 'INACTIVE' : 'ACTIVE',
      notes: row.notes ?? '',
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSaving(false);
  };

  const onSubmitModal = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    const engTrim = form.englishName.trim();
    const payload = {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      englishName: engTrim ? engTrim : null,
      company: form.company.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      status: form.status === 'INACTIVE' ? 'INACTIVE' : 'ACTIVE',
      notes: form.notes.trim() ? form.notes : null,
    };
    try {
      if (modalMode === 'create') {
        await createReferralPanelContact(payload);
      } else {
        await updateReferralPanelContact(editId, payload);
      }
      await load();
      closeModal();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (row) => {
    if (!window.confirm(`Remove ${contactLabel(row)} from the referral panel? This cannot be undone.`)) {
      return;
    }
    setError('');
    try {
      await deleteReferralPanelContact(row.id);
      await load();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  return (
    <div className="ref-panel">
      <header className="ref-panel__header">
        <div>
          <h1 className="ref-panel__title">Referral Panel</h1>
          <p className="ref-panel__lead">
            Referral contacts grouped by company. Name is shown as Last name (caps), First name (title case),
            optional English name in parentheses. Visible to administrators only.
          </p>
        </div>
        <button type="button" className="ref-panel__btn ref-panel__btn--primary" onClick={openCreate}>
          Add contact
        </button>
      </header>

      {error ? <div className="ref-panel__error">{error}</div> : null}

      <div className="ref-panel__body">
        {loading ? (
          <p className="ref-panel__muted">Loading contacts…</p>
        ) : totalContacts === 0 ? (
          <div className="ref-panel__empty">
            No contacts yet. Use &quot;Add contact&quot; to add people who give referrals for others.
          </div>
        ) : (
          groups.map((group) => (
            <section key={group.company} className="ref-panel__company-section">
              <h2 className="ref-panel__company-heading">{group.company || '—'}</h2>
              <div className="ref-panel__table-wrap">
                <table className="ref-panel__table">
                  <colgroup>
                    <col className="ref-panel__col ref-panel__col--name" />
                    <col className="ref-panel__col ref-panel__col--email" />
                    <col className="ref-panel__col ref-panel__col--status" />
                    <col className="ref-panel__col ref-panel__col--notes" />
                    <col className="ref-panel__col ref-panel__col--actions" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Status</th>
                      <th>Notes</th>
                      <th className="ref-panel__th-actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {(group.contacts || []).map((r) => (
                      <tr key={r.id} className={r.status === 'INACTIVE' ? 'ref-panel__tr--inactive' : undefined}>
                        <td>
                          <span className="ref-panel__name">{r.displayName || contactLabel(r)}</span>
                        </td>
                        <td>
                          {r.email ? (
                            <a className="ref-panel__link" href={`mailto:${encodeURIComponent(r.email)}`}>
                              {r.email}
                            </a>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td>
                          <span
                            className={
                              r.status === 'INACTIVE'
                                ? 'ref-panel__badge ref-panel__badge--inactive'
                                : 'ref-panel__badge ref-panel__badge--active'
                            }
                          >
                            {r.status === 'INACTIVE' ? 'Inactive' : 'Active'}
                          </span>
                        </td>
                        <td className="ref-panel__notes-cell" title={r.notes || undefined}>
                          {r.notes ? (
                            <span className="ref-panel__notes-preview">{r.notes}</span>
                          ) : (
                            <span className="ref-panel__notes-empty">—</span>
                          )}
                        </td>
                        <td className="ref-panel__actions">
                          <button type="button" className="ref-panel__btn ref-panel__btn--ghost" onClick={() => openEdit(r)}>
                            Edit
                          </button>
                          <button type="button" className="ref-panel__btn ref-panel__btn--danger" onClick={() => onDelete(r)}>
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ))
        )}
      </div>

      {modalOpen && (
        <div className="ref-panel__backdrop" role="presentation" onClick={closeModal}>
          <div
            className="ref-panel__modal ref-panel__modal--wide"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ref-panel-modal-title"
            onClick={(ev) => ev.stopPropagation()}
          >
            <div className="ref-panel__modal-head">
              <h2 id="ref-panel-modal-title">{modalMode === 'create' ? 'Add contact' : 'Edit contact'}</h2>
              <button type="button" className="ref-panel__modal-close" onClick={closeModal} aria-label="Close">
                ×
              </button>
            </div>
            <form onSubmit={onSubmitModal} className="ref-panel__form">
              <div className="ref-panel__field-row">
                <label className="ref-panel__field">
                  <span>First name</span>
                  <input
                    type="text"
                    value={form.firstName}
                    onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))}
                    required
                    maxLength={128}
                    autoComplete="given-name"
                  />
                </label>
                <label className="ref-panel__field">
                  <span>Last name</span>
                  <input
                    type="text"
                    value={form.lastName}
                    onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))}
                    required
                    maxLength={128}
                    autoComplete="family-name"
                  />
                </label>
              </div>
              <label className="ref-panel__field">
                <span>English name</span>
                <input
                  type="text"
                  value={form.englishName}
                  onChange={(e) => setForm((f) => ({ ...f, englishName: e.target.value }))}
                  maxLength={128}
                  placeholder="Optional"
                  autoComplete="off"
                />
              </label>
              <label className="ref-panel__field">
                <span>Company</span>
                <input
                  type="text"
                  value={form.company}
                  onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))}
                  required
                  maxLength={255}
                  autoComplete="organization"
                />
              </label>
              <label className="ref-panel__field">
                <span>Email</span>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  required
                  maxLength={320}
                  autoComplete="email"
                />
              </label>
              <label className="ref-panel__field">
                <span>Phone</span>
                <input
                  type="text"
                  value={form.phone}
                  onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                  required
                  maxLength={64}
                  autoComplete="tel"
                />
              </label>
              <label className="ref-panel__field">
                <span>Status</span>
                <select
                  value={form.status}
                  onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                  aria-label="Contact status"
                >
                  <option value="ACTIVE">Active</option>
                  <option value="INACTIVE">Inactive</option>
                </select>
              </label>
              <label className="ref-panel__field">
                <span>Notes</span>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                  rows={5}
                  maxLength={NOTES_MAX_LEN}
                  placeholder="Internal notes (optional)"
                  className="ref-panel__textarea"
                />
              </label>
              <p className="ref-panel__hint">
                Display uses: LAST NAME (all caps), First name (title case){' '}
                {form.englishName.trim() ? ', English name (title case) in parentheses' : '; add English name to show it in parentheses'}.
              </p>
              <div className="ref-panel__form-actions">
                <button type="button" className="ref-panel__btn ref-panel__btn--ghost" onClick={closeModal} disabled={saving}>
                  Cancel
                </button>
                <button type="submit" className="ref-panel__btn ref-panel__btn--primary" disabled={saving}>
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
